import React, { createContext, useContext, useEffect, useState } from "react";
import { Locale, translations } from "./locales";

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
  t: typeof translations.en;
}

const I18nContext = createContext<I18nContextType | null>(null);

const STORAGE_KEY = "groweb_lang";

export function I18nProvider({ children }: { children: React.ReactNode }) {
  // Requirement: Default to English, optional Chinese
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "zh" || saved === "en") {
        return saved;
      }
    }
    return "en";
  });

  const setLocale = (l: Locale) => {
    setLocaleState(l);
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, l);
      document.documentElement.lang = l === "zh" ? "zh-CN" : "en";
    }
  };

  const toggleLocale = () => {
    setLocale(locale === "en" ? "zh" : "en");
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
    }
  }, [locale]);

  const activeTranslations = translations[locale] as typeof translations.en;

  return (
    <I18nContext.Provider value={{ locale, setLocale, toggleLocale, t: activeTranslations }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}

export function LanguageSwitcher({ className = "" }: { className?: string }) {
  const { locale, setLocale } = useI18n();

  return (
    <div
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: "rgba(0,0,0,0.04)",
        padding: "2px",
        borderRadius: "6px",
        fontSize: "12px",
        fontWeight: "600",
      }}
    >
      <button
        type="button"
        onClick={() => setLocale("en")}
        style={{
          border: "none",
          padding: "3px 8px",
          borderRadius: "4px",
          background: locale === "en" ? "#ffffff" : "transparent",
          color: locale === "en" ? "#2563eb" : "#64748b",
          boxShadow: locale === "en" ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
          cursor: "pointer",
          fontSize: "12px",
        }}
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => setLocale("zh")}
        style={{
          border: "none",
          padding: "3px 8px",
          borderRadius: "4px",
          background: locale === "zh" ? "#ffffff" : "transparent",
          color: locale === "zh" ? "#2563eb" : "#64748b",
          boxShadow: locale === "zh" ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
          cursor: "pointer",
          fontSize: "12px",
        }}
      >
        中文
      </button>
    </div>
  );
}
